import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { CardTextArea, CardTextAreaRef } from '../input-text';
import { useCard } from 'src/service';

export type EffectInputGroupRef = {
    setValue: (value: string) => void,
};
export type EffectInputGroup = {} & Pick<CardTextArea, 'onTakePicker'>;
export const EffectInputGroup = forwardRef<EffectInputGroupRef, EffectInputGroup>(({
    onTakePicker,
}, ref) => {
    const getUpdater = useCard(state => state.getUpdater);
    const effect = useCard(state => state.card.effect);
    const effectInputRef = useRef<CardTextAreaRef>(null);
    const isImportingRef = useRef(false);

    const changeEffect = useMemo(() => {
        const updater = getUpdater('effect', undefined, 'debounce');
        return (e: { target: { value: string } }) => {
            // Don't update if we're currently importing - the import already set the correct value
            if (isImportingRef.current) {
                isImportingRef.current = false;
                return;
            }
            updater(e);
        };
    }, [getUpdater]);

    useEffect(() => {
        const pickerRef = effectInputRef.current?.getPickerRef();

        if (pickerRef) onTakePicker?.(pickerRef);
    /** No need to depend on handler */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync the input when the card effect changes externally (e.g., from import)
    useEffect(() => {
        isImportingRef.current = true;
        effectInputRef.current?.setValue(effect);
        // Reset the flag after a short delay to allow the onChange to be skipped
        setTimeout(() => {
            isImportingRef.current = false;
        }, 300);
    }, [effect]);

    useImperativeHandle(ref, () => ({
        setValue: (value) => {
            isImportingRef.current = true;
            effectInputRef.current?.setValue(value);
            setTimeout(() => {
                isImportingRef.current = false;
            }, 300);
        }
    }));

    return <CardTextArea ref={effectInputRef}
        id="effect"
        allowHotkey
        defaultValue={effect}
        onChange={changeEffect}
        onTakePicker={onTakePicker}
        autoSize={{
            minRows: 9,
        }}
    />;
});